import { useCallback, useEffect, useState } from 'react';
import {
  NavLink,
  useParams,
  Outlet,
  generatePath,
  useLocation,
  useNavigate,
} from 'react-router-dom';
import { useForm } from 'react-hook-form';
import isEqual from 'lodash.isequal';
import { v4 as uuid } from 'uuid';
import {
  IconArrowLeft,
  IconLoader,
  IconSelected,
  ajvResolver,
  Popup,
  ValidatorsProps,
  IconArrowRight,
  usePrevious,
  PermissionEnum,
  PermissionDenied,
  ResponseError,
  ButtonPrimary,
  responseErrorsResolver,
  BlacklistUserInfoData,
  formatBlacklistItem,
  BlacklistData,
} from '@ui/src';
import { useAppDispatch, useAppSelector } from '@store';
import {
  clearNewApplicationInfoState,
  checkChatMessages,
  setHintVisible,
  setMatchSelected,
  updateStepsStatus,
  onGroupSelect,
  setSelected,
  setAllowedDocumentsCategories,
} from './store/slices';
import StepItem from '../../../components/new-application/step-item';
import StepOne from '../../../components/new-application/individual/step-one';
import StepTwo from '../../../components/new-application/individual/step-two';
import StepThree from '../../../components/new-application/individual/step-three';
import StepFour from '../../../components/new-application/individual/step-four';
import StepFive from '../../../components/new-application/ongoing-monitoring';
import NewApplicationInfoTop from '../new-application-info-top';
import {
  useLazyGetNewApplicationsValidatorsQuery,
  useAddNewApplicationCommentMutation,
  useLazyGetNewApplicationInfoQuery,
  useUpdateNewApplicationMutation,
  useUpdateIndividualApplicationStatusMutation,
  useLazyGetNewApplicationSystemLogsQuery,
  useLazyGetIndividualNewApplicationDocumentsQuery,
  useLazyGetApplicationAgreementsQuery,
  useLazyGetApplicationChatQuery,
  useLazyGetApplicationOngoingMonitoringQuery,
  useApplyApplicationOngoingWarningActionMutation,
  useBatchApplyApplicationOngoingWarningActionMutation,
  useLazyGetApplicationOngoingStatusQuery,
  useRestartOngoingCheckingMutation,
  useLazyGetApplicationQuestionnarieQuery,
  useUpdateInividualQuestionnarieMutation,
  useResetApplicationWarningActionMutation,
  useLazyGetNewApplicationOrganizationQuery,
  useLazyGetApplicationWarningGroupWithPaginationQuery,
  useLazyGetApplicationOngoingWarningQuery,
  useLazyGetApplicationRiskScoreQuery,
  useCreateApplicationRiskScoreMutation,
  useLazyGetApplicationAmlUpdatingItemQuery,
  useLazyGetApplicationLastCalculatingScoreQuery,
  useLazyGetSourceOfWealthForIndividualDictionaryQuery,
  useLazyGetSourceOfIncomeDictionaryQuery,
  useLazyGetCustomerSourceOfWealthQuery,
  useLazyGetIndividualAccountsCurrenciesQuery,
  useCreateIndividualAccountsCurrencyMutation,
  useUpdateIndividualAccountsCurrencyMutation,
  useDeleteIndividualAccountsCurrencyMutation,
  useLazyGetIndividualAccountsFlowsQuery,
  useCreateIndividualAccountsFlowMutation,
  useUpdateIndividualAccountsFlowMutation,
  useDeleteIndividualAccountsFlowMutation,
  useLazyGetIndividualExpectedFundsOriginsQuery,
  useCreateIndividualExpectedFundsOriginsMutation,
  useUpdateIndividualExpectedFundsOriginsMutation,
  useDeleteIndividualExptectedFundsOriginsMutation,
  useLazyGetCustomerSourceOfFundsQuery,
  useLazyGetCRSAndFactaQuery,
  useCreateCRSAndFactaMutation,
  useUpdateCRSAndFactaMutation,
  useDeleteCRSAndFactaMutation,
} from '../store/services';
import {
  NewApplicationStepStatus,
  FormValues,
  OnSubmitType,
  ApplicationType,
  NewApplicationStatus,
  AccountsCurrenciesFormValues,
  AccountsFlowsFormValues,
  ExpectedFundsOriginsFormValues,
  CRSAndFatcaFormValues,
  AccountsCurrencies,
  AllowedCategory,
} from '../types';
import { updateApplicationStepStatuses } from './helpers/update-application-step-status';
import getIndividualApplicationBody from './helpers/get-individual-application-body';
import {
  COFAndIndividualQuestionnarieFormValues,
  CustomerScore,
  CustomerScoreUpdateStatus,
  OngoingCheckingStatus,
  OngoingMonitring,
  OngoingStatus,
} from '../../customers/types';
import {
  useGetCustomersInfoDictionariesQuery,
  useLazyGetCustomersEmployeesListQuery,
} from '../../../store/dictionaries/services';
import getCofandIndividualQuestionnarieBody from './helpers/get-qestionnarie-body';

import ROUTES from '../../../routes';

import cn from '../new-application.module.css';

let retryTimeout: NodeJS.Timeout;
let timeInterval: NodeJS.Timeout;

const NewApplicationInfo = () => {
  const [defaultFormValues, setDefaultFormValues] = useState({});
  const [defaultQuestionnarieFormValues, setDefaultQuestionnarieFormValues] =
    useState({});
  const [applicationLoadErrorVisible, setApplicationLoadErrorVisible] =
    useState(false);
  const [selectedWarningId, setSelectedWarningId] = useState('');
  const [successMessageVisible, setSuccessMessageVisible] = useState(false);
  const [haveSecondPassport, setHaveSecondPassport] = useState(false);

  const {
    newApplications: {
      individualInfo: {
        newApplicationInfo,
        hintVisible,
        stepsStatus: applicationStepsStatus,
        systemLogs,
        documents,
        selectedDocumentsCounts,
        agreements,
        ongoingMonitoring,
        ongoingCheckingStatus,
        questionnarie,
        changeLogs,
        changeLogsPagination,
        riskScore,
        amlUpdateStatus,
        sourceOfWealth,
        sourceOfIncome,
        sourceOfWealthForIndividual,
        accountsCurrencies,
        questionnarieId,
        accountsFlows,
        expectedFundsOrigins,
        customerSourceOfFunds,
        crsAndFatca,
        allowedDocumentsCategories,
      },
      list: { validators, applicationChat },
    },
    customersDictionaries: {
      countries,
      activePermissions,
      sourcesOfFunds,
      clientsActivities,
      infoDictionariesIsLoading,
      employeesList,
      currentEmployee,
    },
  } = useAppSelector((state) => state);
  const { baseCurrency, currenciesList, currencies } = useAppSelector(
    (state) => state.root,
  );

  const { search } = useLocation();

  const [dirtyPopupVisible, setDirtyPopupVisible] = useState(false);
  const [statusChangeError, setStatusChangeError] = useState('');
  const [userCreated, setUserCreated] = useState(false);
  const [selectedWarningGroup, setSelectedWarningGroup] = useState('');
  const [blacklistData, setBlacklistData] = useState<BlacklistData | undefined>(
    undefined,
  );

  const [blacklistDataVisible, setBlacklistDataVisible] =
    useState<boolean>(false);
  const [blacklistItem, setBlacklistDataItem] =
    useState<BlacklistUserInfoData>();
  const [blacklistItemVisible, setBlacklistItemVisible] = useState(false);
  const [accountsCurrenciesValues, setAccountsCurrenciesValues] = useState<
    AccountsCurrenciesFormValues[]
  >([
    {
      id: 'accounts-currency-1',
      accountOpeningPurpose: '',
      currencyId: '',
      plannedTransactionNature: '',
    },
  ]);
  const [selectedAccountsCurrencyId, setSelectedAccountsCurrencyId] =
    useState('');
  const [accountsFlowsValues, setAccountsFlowsValues] = useState<
    AccountsFlowsFormValues[]
  >([]);
  const [expectedFundsOriginsValues, setExpectedFundsOriginsValues] = useState<
    ExpectedFundsOriginsFormValues[]
  >([
    {
      id: 'expected-funds-origins-1',
      currencyId: '',
      mainCounterpartiesAndWebsites: '',
      countryOfIncorporationId: '',
      counterpartyBankCountryIds: [],
      activityCountryIds: [],
      mainActivities: '',
      transactionReason: '',
      transactionType: 'INCOMING',
    },
    {
      id: 'expected-funds-origins-2',
      currencyId: '',
      mainCounterpartiesAndWebsites: '',
      countryOfIncorporationId: '',
      counterpartyBankCountryIds: [],
      activityCountryIds: [],
      mainActivities: '',
      transactionReason: '',
      transactionType: 'OUTGOING',
    },
  ]);
  const [selectedExpectedFundsOriginsId, setSelectedFundsOriginsId] =
    useState('');
  const [crsAndFatcaValues, setCrsAndFatcaValues] = useState<
    CRSAndFatcaFormValues[]
  >([
    {
      id: 'crs-and-fatca-1',
      residenceCountryId: '',
      taxIdentificationNumber: '',
      noTinReason: '',
    },
  ]);
  const [selectedCrsAndFatcaId, setSelectedCrsAndFatcaId] = useState('');

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors, isDirty },
    setError,
    clearErrors,
  } = useForm<FormValues>({
    resolver: ajvResolver(
      validators?.updateClientApplicationSchema as ValidatorsProps,
    ),
  });
  const {
    control: questionnarieControl,
    reset: questionnarieReset,
    clearErrors: questionnarieClearErrors,
    formState: { errors: questionnarieErrors, isDirty: questionnarieIsDirty },
    watch: questionnarieWatch,
    getValues: questionnarieGetValues,
    setError: questionnarieSetError,
    setValue: questionnarieSetValue,
  } = useForm<COFAndIndividualQuestionnarieFormValues>();

  const [popupVisible, setPopupVisible] = useState(false);
  const [applyError, setApplyError] = useState('');

  useGetCustomersInfoDictionariesQuery();

  const fields = watch();
  const questionnarieFields = questionnarieWatch();
  const prevClientActivitiy = usePrevious(fields.clientActivityId);
  const prevSourceOfFunds = usePrevious(fields.sourceOfFundsIds);

  const employmentActivity = clientsActivities?.find(
    (item) => item.name === 'Employment',
  );
  const otherActivity = clientsActivities?.find(
    (item) => item.name === 'Other',
  );
  const businessOwnerActivity = clientsActivities?.find(
    (item) => item.name === 'Business Owner',
  );

  const otherSourceOfFunds = sourcesOfFunds?.find(
    (item) => item.name === 'Other',
  );

  const dispatch = useAppDispatch();
  const { applicationId } = useParams();

  const navigate = useNavigate();

  const stepsStatus = updateApplicationStepStatuses({
    values: fields,
    stepsStatus: applicationStepsStatus,
    sourcesOfFunds,
    clientsActivities,
    agreements,
    ongoingMonitoring,
    ongoingCheckingStatus,
    applicationScheme: newApplicationInfo?.applicationScheme,
    questionnarieFields,
    accountsCurrenciesValues,
    expectedFundsOriginsValues,
    accountsFlowsValues,
    crsAndFatcaValues,
    haveSecondPassport,
    documents,
  });

  const prevStepsStatus = usePrevious(stepsStatus);
  const prevApplicationId = usePrevious(applicationId);

  const [getApplicationChat] = useLazyGetApplicationChatQuery();
  const [getInfo, { isLoading, isFetching }] =
    useLazyGetNewApplicationInfoQuery();
  const [getApplicationOrganization] =
    useLazyGetNewApplicationOrganizationQuery();
  const [updateInfo, { isLoading: requestInProgress }] =
    useUpdateNewApplicationMutation();
  const [getValidators] = useLazyGetNewApplicationsValidatorsQuery();
  const [createNewIndividualCustomer, { isLoading: createInProgress }] =
    useUpdateIndividualApplicationStatusMutation();
  const [getApplicationRiskScore, { isLoading: riskScoreIsLoading }] =
    useLazyGetApplicationRiskScoreQuery();
  const [getApplicationAmlUpdatingItem] =
    useLazyGetApplicationAmlUpdatingItemQuery();
  const [getApplicationLastCalculaingScore] =
    useLazyGetApplicationLastCalculatingScoreQuery();
  const [
    createApplicationScore,
    { isSuccess, isLoading: riskScoreRequestInProgress },
  ] = useCreateApplicationRiskScoreMutation();
  const [
    rejectApplication,
    {
      isLoading: rejectInProgress,
      isSuccess: rejectIsSuccess,
      isError: rejectIsError,
    },
  ] = useUpdateIndividualApplicationStatusMutation();
  const [backToIncomplete, { isLoading: backToIncompleteInProgress }] =
    useUpdateIndividualApplicationStatusMutation();
  const [
    addResponsibleToApplication,
    {
      isLoading: addResponsibleInProgress,
      isSuccess: addResponsibleIsSuccess,
      isError: addResponsibleIsError,
    },
  ] = useUpdateIndividualApplicationStatusMutation();
  const [
    getApplicationOngoingMonitoring,
    { isLoading: ongoingIsLoading, isFetching: ongoingIsFetching },
  ] = useLazyGetApplicationOngoingMonitoringQuery();

  const [getSystemLogs] = useLazyGetNewApplicationSystemLogsQuery();
  const [
    addComment,
    { isLoading: sendCommentInProgress, isSuccess: sendCommentIsSuccess },
  ] = useAddNewApplicationCommentMutation();
  const [getIndividualApplicationDocuments] =
    useLazyGetIndividualNewApplicationDocumentsQuery();
  const [getApplicationAgreements] = useLazyGetApplicationAgreementsQuery();
  const [getCustomersEmployeesList] = useLazyGetCustomersEmployeesListQuery();
  const [
    applyApplicationOngoingWarning,
    { isLoading: applyWarningInProgress },
  ] = useApplyApplicationOngoingWarningActionMutation();
  const [
    batchApplyApplicationOngoingWarning,
    {
      isLoading: batchApplyWarningInProgress,
      isSuccess: batchApplyWarningIsSuccess,
    },
  ] = useBatchApplyApplicationOngoingWarningActionMutation();
  const [getOngoingMonitoringStatus, { isLoading: ongoingStatusIsLoading }] =
    useLazyGetApplicationOngoingStatusQuery();
  const [restartOngoingChecking, { isLoading: restartInProgress }] =
    useRestartOngoingCheckingMutation();
  const [
    getApplicationQuestionnarie,
    { isLoading: questionnarieIsLoading, isFetching: questionnarieIsFetching },
  ] = useLazyGetApplicationQuestionnarieQuery();
  const [
    updateIndividualQuestionnarie,
    { isLoading: questionnarieUpdateInProgress },
  ] = useUpdateInividualQuestionnarieMutation();
  const [
    resetApplicationWarningsWarning,
    {
      isLoading: resetTransactionsWarningIsLoading,
      isSuccess: resetTransactionsWarningIsSuccess,
    },
  ] = useResetApplicationWarningActionMutation();
  const [
    getApplicationWarning,
    {
      isLoading: paginationInProgress,
      isFetching: paginationIsFetching,
      isSuccess: paginationIsSuccess,
    },
  ] = useLazyGetApplicationWarningGroupWithPaginationQuery();
  const [
    getApplicationOngoingWarningById,
    { isLoading: isWarningByIdLoading, isFetching: isWarningByIdFetching },
  ] = useLazyGetApplicationOngoingWarningQuery();
  const [getSourceOfWealthForIndividualDictionary] =
    useLazyGetSourceOfWealthForIndividualDictionaryQuery();
  const [getSourceOfIncomeDictionary] =
    useLazyGetSourceOfIncomeDictionaryQuery();
  const [getCustomerSourceOfWealth] = useLazyGetCustomerSourceOfWealthQuery();
  const [getCustomerSourceOfFunds] = useLazyGetCustomerSourceOfFundsQuery();

  const [
    getAccountsCurrencies,
    {
      isLoading: accountsCurrenciesIsLoading,
      isFetching: accountsCurrenciesIsFetching,
    },
  ] = useLazyGetIndividualAccountsCurrenciesQuery();
  const [
    createAccountsCurrencies,
    { isLoading: createAccountsCurrenciesIsProgress },
  ] = useCreateIndividualAccountsCurrencyMutation();
  const [
    updateAccountsCurrencies,
    { isLoading: updateAccountsCurrenciesInProgress },
  ] = useUpdateIndividualAccountsCurrencyMutation();
  const [
    deleteAccountsCurrencies,
    {
      isLoading: deleteAccountsCurrenciesInProgress,
      isSuccess: deleteAccountsCurrenciesIsSuccess,
    },
  ] = useDeleteIndividualAccountsCurrencyMutation();
  const [
    getAccountsFlows,
    { isLoading: accountsFlowsIsLoading, isFetching: accountsFlowsIsFetching },
  ] = useLazyGetIndividualAccountsFlowsQuery();
  const [createAccountsFlows, { isLoading: createAccountsFlowsIsProgress }] =
    useCreateIndividualAccountsFlowMutation();
  const [updateAccountsFlows, { isLoading: updateAccountsFlowsInProgress }] =
    useUpdateIndividualAccountsFlowMutation();
  const [deleteAccountsFlows] = useDeleteIndividualAccountsFlowMutation();
  const [
    getExpectedFundsOrigins,
    {
      isLoading: expectedFundsOriginsIsLoading,
      isFetching: expectedFundsOriginsIsFetching,
    },
  ] = useLazyGetIndividualExpectedFundsOriginsQuery();
  const [
    createExpectedFundsOrigins,
    { isLoading: createExpectedFundsOriginsInProgress },
  ] = useCreateIndividualExpectedFundsOriginsMutation();
  const [
    updateExpectedFundsOrigins,
    { isLoading: updateExpectedFundsOriginsInProgress },
  ] = useUpdateIndividualExpectedFundsOriginsMutation();
  const [
    deleteExpectedFundsOrigins,
    {
      isLoading: deleteExpectedFundsOriginsInProgress,
      isSuccess: deleteExpectedFundsOriginsIsSuccess,
    },
  ] = useDeleteIndividualExptectedFundsOriginsMutation();
  const [
    getCrsAndFatca,
    { isLoading: crsAndFatcaIsLoading, isFetching: crsAndFatcaIsFetching },
  ] = useLazyGetCRSAndFactaQuery();
  const [createCrsAndFatca, { isLoading: createCrsAndFatcaIsProgress }] =
    useCreateCRSAndFactaMutation();
  const [updateCrsAndFatca, { isLoading: updateCrsAndFatcaInProgress }] =
    useUpdateCRSAndFactaMutation();
  const [
    deleteCrsAndFatca,
    {
      isLoading: deleteCrsAndFatcaInProgress,
      isSuccess: deleteCrsAndFatcaIsSuccess,
    },
  ] = useDeleteCRSAndFactaMutation();

  const onUpdate = useCallback(() => {
    createApplicationScore(applicationId as string);
  }, [applicationId]);

  const onSaveCRSAndFatca = async () => {
    const crsAndFatcaForCreation: CRSAndFatcaFormValues[] = [];
    const crsAndFatcaForForUpdate: CRSAndFatcaFormValues[] = [];

    crsAndFatcaValues?.forEach((item) => {
      if (item.id?.includes('crs-and-fatca')) {
        crsAndFatcaForCreation.push(item);
      } else {
        const currentcrsAndFatca = crsAndFatca?.find(
          (value) => value.id === item.id,
        );

        if (currentcrsAndFatca) {
          if (
            !isEqual(item, {
              id: currentcrsAndFatca.id,
              residenceCountryId: currentcrsAndFatca?.residenceCountryId,
              taxIdentificationNumber:
                currentcrsAndFatca?.taxIdentificationNumber,
              noTinReason: currentcrsAndFatca?.noTinReason,
            })
          ) {
            crsAndFatcaForForUpdate.push(item);
          }
        }
      }
    });

    if (crsAndFatcaForCreation?.length) {
      await Promise.all(
        crsAndFatcaForCreation
          ?.filter((item) => item.residenceCountryId)
          ?.map(async (item) => {
            const response = await createCrsAndFatca({
              id: questionnarieId as string,
              body: {
                residenceCountryId: item.residenceCountryId,
                taxIdentificationNumber: item.taxIdentificationNumber,
                noTinReason: item.noTinReason,
              },
            });

            return response;
          }),
      );
    }

    if (crsAndFatcaForForUpdate?.length) {
      await Promise.all(
        crsAndFatcaForForUpdate?.map(async (item) => {
          const response = await updateCrsAndFatca({
            id: item.id as string,
            body: {
              residenceCountryId: item.residenceCountryId,
              taxIdentificationNumber: item.taxIdentificationNumber,
              noTinReason: item.noTinReason,
            },
          });

          return response;
        }),
      );
    }
  };

  const onSaveExpectedFundsOrigins = async () => {
    const expectedFundsOriginsForCreation: ExpectedFundsOriginsFormValues[] =
      [];
    const expectedFundsOriginsForUpdate: ExpectedFundsOriginsFormValues[] = [];

    expectedFundsOriginsValues?.forEach((item) => {
      if (item.id?.includes('expected-funds-origins')) {
        expectedFundsOriginsForCreation.push(item);
      } else {
        const currentExpectedFundsOrigins = expectedFundsOrigins?.find(
          (value) => value.id === item.id,
        );

        if (currentExpectedFundsOrigins) {
          if (
            !isEqual(item, {
              id: currentExpectedFundsOrigins.id,
              currencyId: currentExpectedFundsOrigins.currencyId,
              mainCounterpartiesAndWebsites:
                currentExpectedFundsOrigins.mainCounterpartiesAndWebsites,
              countryOfIncorporationId:
                currentExpectedFundsOrigins.countryOfIncorporationId,
              counterpartyBankCountryIds:
                currentExpectedFundsOrigins.counterpartyBankCountryIds,
              activityCountryIds:
                currentExpectedFundsOrigins.activityCountryIds,
              mainActivities: currentExpectedFundsOrigins.mainActivities,
              transactionReason: currentExpectedFundsOrigins.transactionReason,
              transactionType: currentExpectedFundsOrigins.transactionType,
            })
          ) {
            expectedFundsOriginsForUpdate.push(item);
          }
        }
      }
    });

    if (expectedFundsOriginsForCreation?.length) {
      await Promise.all(
        expectedFundsOriginsForCreation
          ?.filter((item) => item.currencyId)
          ?.map(async (item) => {
            const response = await createExpectedFundsOrigins({
              id: questionnarieId as string,
              body: {
                currencyId: item.currencyId,
                mainCounterpartiesAndWebsites:
                  item.mainCounterpartiesAndWebsites || '',
                countryOfIncorporationId: item.countryOfIncorporationId || '',
                counterpartyBankCountryIds:
                  item.counterpartyBankCountryIds || '',
                activityCountryIds: item.activityCountryIds || '',
                mainActivities: item.mainActivities || '',
                transactionReason: item.transactionReason || '',
                transactionType: item.transactionType || '',
              },
            });

            return response;
          }),
      );
    }

    if (expectedFundsOriginsForUpdate?.length) {
      await Promise.all(
        expectedFundsOriginsForUpdate?.map(async (item) => {
          const response = await updateExpectedFundsOrigins({
            id: item.id as string,
            body: {
              currencyId: item.currencyId,
              mainCounterpartiesAndWebsites:
                item.mainCounterpartiesAndWebsites || '',
              countryOfIncorporationId: item.countryOfIncorporationId || '',
              counterpartyBankCountryIds: item.counterpartyBankCountryIds || '',
              activityCountryIds: item.activityCountryIds || '',
              mainActivities: item.mainActivities || '',
              transactionReason: item.transactionReason || '',
              transactionType: item.transactionType || '',
            },
          });

          return response;
        }),
      );
    }
  };

  const onSaveAccountsCurrencies = async () => {
    const accountsCurrenciesForCreation: AccountsCurrenciesFormValues[] = [];
    const accountsCurrenciesForUpdate: AccountsCurrenciesFormValues[] = [];

    accountsCurrenciesValues?.forEach((item) => {
      if (item.id?.includes('accounts-currency')) {
        accountsCurrenciesForCreation.push(item);
      } else {
        const currentAccountsCurrency = accountsCurrencies?.find(
          (currency) => currency.id === item.id,
        );

        if (currentAccountsCurrency) {
          if (
            !isEqual(item, {
              id: currentAccountsCurrency.id,
              currencyId: currentAccountsCurrency.currencyId,
              accountOpeningPurpose:
                currentAccountsCurrency.accountOpeningPurpose,
              plannedTransactionNature:
                currentAccountsCurrency.plannedTransactionNature,
            })
          ) {
            accountsCurrenciesForUpdate.push(item);
          }
        }
      }
    });

    if (accountsCurrenciesForCreation?.length) {
      await Promise.all(
        accountsCurrenciesForCreation
          ?.filter((item) => item.currencyId)
          ?.map(async (item) => {
            const response = await createAccountsCurrencies({
              id: questionnarieId as string,
              body: {
                currencyId: item.currencyId,
                accountOpeningPurpose: item.accountOpeningPurpose || '',
                plannedTransactionNature: item.plannedTransactionNature || '',
              },
            });

            return response;
          }),
      );
    }

    if (accountsCurrenciesForUpdate?.length) {
      await Promise.all(
        accountsCurrenciesForUpdate?.map(async (item) => {
          const response = await updateAccountsCurrencies({
            id: item.id as string,
            body: {
              currencyId: item.currencyId,
              accountOpeningPurpose: item.accountOpeningPurpose || '',
              plannedTransactionNature: item.plannedTransactionNature || '',
            },
          });

          return response;
        }),
      );
    }
  };

  const onSaveAccountsFlows = async () => {
    const accountsFlowsForCreation: AccountsFlowsFormValues[] = [];
    const accountsFlowsForUpdate: AccountsFlowsFormValues[] = [];

    accountsFlowsValues?.forEach((item) => {
      if (item.id?.includes('accounts-flow')) {
        accountsFlowsForCreation.push(item);
      } else {
        const currentAccountsFlow = accountsFlows?.find(
          (flow) => flow.id === item.id,
        );

        if (currentAccountsFlow) {
          if (
            !isEqual(item, {
              id: currentAccountsFlow.id,
              transactionType: currentAccountsFlow.transactionType,
              currencyId: currentAccountsFlow.currencyId,
              expectedAnnualCreditTurnover:
                currentAccountsFlow.expectedAnnualCreditTurnover.toString(),
              maxTransferAmountPerTransaction:
                currentAccountsFlow.maxTransferAmountPerTransaction.toString(),
              transactionFrequency:
                currentAccountsFlow.transactionFrequency.toString(),
              transactionFrequencyPeriodMonths:
                currentAccountsFlow.transactionFrequencyPeriodMonths.toString(),
            })
          ) {
            accountsFlowsForUpdate.push(item);
          }
        }
      }
    });

    if (
      accountsFlowsForCreation?.filter(
        (item) =>
          item.expectedAnnualCreditTurnover &&
          item.maxTransferAmountPerTransaction &&
          item.transactionFrequency &&
          item.transactionFrequencyPeriodMonths,
      )?.length
    ) {
      await Promise.all(
        accountsFlowsForCreation
          ?.filter(
            (item) =>
              item.expectedAnnualCreditTurnover &&
              item.maxTransferAmountPerTransaction &&
              item.transactionFrequency &&
              item.transactionFrequencyPeriodMonths,
          )
          ?.map(async (item) => {
            const response = await createAccountsFlows({
              id: questionnarieId as string,
              body: {
                transactionType: item.transactionType,
                currencyId: item.currencyId,
                expectedAnnualCreditTurnover: Number(
                  item.expectedAnnualCreditTurnover,
                ),
                maxTransferAmountPerTransaction: Number(
                  item.maxTransferAmountPerTransaction,
                ),
                transactionFrequency: Number(item.transactionFrequency),
                transactionFrequencyPeriodMonths: Number(
                  item.transactionFrequencyPeriodMonths,
                ),
              },
            });

            return response;
          }),
      );
    }

    if (accountsFlowsForUpdate?.length) {
      await Promise.all(
        accountsFlowsForUpdate?.map(async (item) => {
          const response = await updateAccountsFlows({
            id: item.id as string,
            body: {
              transactionType: item.transactionType,
              currencyId: item.currencyId,
              expectedAnnualCreditTurnover: Number(
                item.expectedAnnualCreditTurnover,
              ),
              maxTransferAmountPerTransaction: Number(
                item.maxTransferAmountPerTransaction,
              ),
              transactionFrequency: Number(item.transactionFrequency),
              transactionFrequencyPeriodMonths: Number(
                item.transactionFrequencyPeriodMonths,
              ),
            },
          });

          return response;
        }),
      );
    }
  };

  const onDeleteAccountCurrencies = async (id: string) => {
    if (id.includes('accounts-currency')) {
      setAccountsCurrenciesValues(
        accountsCurrenciesValues?.filter((item) => item.id !== id),
      );

      const currentAccountsCurrency = accountsCurrenciesValues?.find(
        (item) => item.id === id,
      );

      if (currentAccountsCurrency) {
        const currentAccountsFlows = accountsFlowsValues?.filter(
          (item) => item.currencyId === currentAccountsCurrency?.currencyId,
        );

        const currentExpectedFundsOrigins = expectedFundsOriginsValues?.filter(
          (item) => item.currencyId === currentAccountsCurrency?.currencyId,
        );

        if (currentAccountsFlows?.length) {
          setAccountsFlowsValues(
            accountsFlowsValues?.filter((item) =>
              currentAccountsFlows.every((flow) => item.id !== flow.id),
            ),
          );
        }

        if (currentExpectedFundsOrigins?.length) {
          setExpectedFundsOriginsValues(
            expectedFundsOriginsValues?.filter((item) =>
              currentExpectedFundsOrigins.every(
                (expectedFund) => item.id !== expectedFund.id,
              ),
            ),
          );
        }
      }
    } else {
      setSelectedAccountsCurrencyId(id);

      const currentAccountsCurrency = accountsCurrenciesValues?.find(
        (item) => item.id === id,
      );

      if (currentAccountsCurrency) {
        const currentAccountsFlows = accountsFlowsValues?.filter(
          (item) => item.currencyId === currentAccountsCurrency?.currencyId,
        );

        const currentExpectedFundsOrigins = expectedFundsOriginsValues?.filter(
          (item) => item.currencyId === currentAccountsCurrency?.currencyId,
        );

        if (currentAccountsFlows?.length) {
          await Promise.all(
            currentAccountsFlows
              ?.filter((item) => !item.id?.includes('accounts-flow'))
              ?.map(async (item) => {
                const response = await deleteAccountsFlows(item.id as string);

                return response;
              }),
          );

          if (
            currentAccountsFlows?.filter((item) =>
              item.id?.includes('accounts-flow'),
            ).length
          ) {
            const notCreatedAccountsFlow = currentAccountsFlows?.filter(
              (item) => item.id?.includes('accounts-flow'),
            );

            setAccountsFlowsValues(
              accountsFlowsValues?.filter((item) =>
                notCreatedAccountsFlow.every((flow) => item.id !== flow.id),
              ),
            );
          }
        }

        if (currentExpectedFundsOrigins?.length) {
          await Promise.all(
            currentExpectedFundsOrigins
              ?.filter((item) => !item.id?.includes('expected-funds-origins'))
              ?.map(async (item) => {
                const response = await deleteExpectedFundsOrigins(
                  item.id as string,
                );

                return response;
              }),
          );

          if (
            currentExpectedFundsOrigins?.filter((item) =>
              item.id?.includes('expected-funds-origins'),
            )?.length
          ) {
            const notCreatedExpectedFundsOrigins =
              currentExpectedFundsOrigins?.filter((item) =>
                item.id?.includes('expected-funds-origins'),
              );

            setExpectedFundsOriginsValues(
              expectedFundsOriginsValues?.filter((item) =>
                notCreatedExpectedFundsOrigins.every(
                  (expectedFundsOrigin) => item.id !== expectedFundsOrigin.id,
                ),
              ),
            );
          }
        }
      }

      await deleteAccountsCurrencies(id);
    }
  };

  const onDeleteExpectedFundsOrigins = async (id: string) => {
    if (id.includes('expected-funds-origins')) {
      setExpectedFundsOriginsValues(
        expectedFundsOriginsValues?.filter((item) => item.id !== id),
      );
    } else {
      setSelectedFundsOriginsId(id);

      await deleteExpectedFundsOrigins(id);
    }
  };

  const onDeleteCRSAndFatca = async (id: string) => {
    if (id.includes('crs-and-fatca')) {
      setCrsAndFatcaValues(crsAndFatcaValues?.filter((item) => item.id !== id));
    } else {
      setSelectedCrsAndFatcaId(id);

      await deleteCrsAndFatca(id);
    }
  };

  const getApplicationRiskScoreAction = useCallback(async () => {
    (await getApplicationRiskScore(applicationId as string)) as {
      data: { data: CustomerScore };
    };
  }, [applicationId]);

  const getApplicationLasCalculaingScoreAction = useCallback(
    async (amlUpdateId: string) => {
      const { data } = await getApplicationAmlUpdatingItem({
        orgId: applicationId as string,
        id: amlUpdateId,
      });

      if (
        data?.data.status === CustomerScoreUpdateStatus.FAIL ||
        data?.data.status === CustomerScoreUpdateStatus.SUCCESS
      ) {
        clearInterval(timeInterval);
      }

      if (data?.data.status === CustomerScoreUpdateStatus.SUCCESS) {
        getApplicationRiskScore(applicationId as string);
      }
    },
    [],
  );

  const getApplicationOngoingWarningByIdAction = async ({
    id,
    warningId,
  }: {
    id: string;
    warningId: string;
  }) => {
    const response = await getApplicationOngoingWarningById({
      appId: applicationId as string,
      warningId,
      id,
    });

    if (
      !blacklistData &&
      !blacklistItem &&
      !blacklistDataVisible &&
      !blacklistItemVisible &&
      response
    ) {
      if (response?.data?.data?.blacklistDoc) {
        setBlacklistData(response?.data?.data?.blacklistDoc);
        setBlacklistDataVisible(true);
      } else if (response?.data?.data?.blacklistItem) {
        setBlacklistDataItem(
          formatBlacklistItem(response?.data?.data?.blacklistItem),
        );
        setBlacklistItemVisible(true);
      }
    }
  };

  const getApplicationWarningsAction = async ({
    warningGroupId,
    cursor,
  }: {
    warningGroupId: string;
    cursor: string;
  }) => {
    setSelectedWarningGroup(warningGroupId);

    await getApplicationWarning({
      id: applicationId as string,
      warningGroupId,
      cursor,
    });
  };

  const batchApplyApplicationOngoingWarningAction = async ({
    action,
    comment,
    matchStatus,
  }: {
    action: string;
    comment: string;
    matchStatus?: string;
  }) => {
    setApplyError('');

    const ongoingGroupsWithoutWanings: OngoingMonitring[] = [];

    ongoingMonitoring?.forEach((item) => {
      item?.warnings?.edges?.forEach((warning) => {
        if (!warning.node.action) {
          ongoingGroupsWithoutWanings.push(item);
        }
      });
    });

    if (ongoingGroupsWithoutWanings && ongoingGroupsWithoutWanings.length) {
      await Promise.all(
        ongoingGroupsWithoutWanings?.map(async (item) => {
          const response = (await batchApplyApplicationOngoingWarning({
            appId: applicationId as string,
            body: {
              warningGroupId: item.id,
              action,
              employeeId: currentEmployee?.id as string,
              comment,
              matchStatus,
            },
          })) as { error: ResponseError };

          if (response.error) {
            if (response.error.data.message) {
              if (
                Array.isArray(response.error.data.message) &&
                response.error.data.message[0].property === 'coreError'
              ) {
                setApplyError(response.error.data.message[0].value as string);
              } else if (typeof response.error.data.message === 'string') {
                setApplyError(response.error.data.message);
              } else {
                setApplyError('Internal Server Error. Try Again');
              }
            }
          }

          return response;
        }),
      );
    }
  };

  const onResetTransactionWarningAction = async (
    warningId: string,
    id: string,
  ) => {
    setSelectedWarningId(warningId);

    const response = (await resetApplicationWarningsWarning({
      appId: applicationId as string,
      id,
      warningId,
    })) as { error: ResponseError };

    if (response.error) {
      if (response.error.data.message) {
        if (
          Array.isArray(response.error.data.message) &&
          response.error.data.message[0].property === 'coreError'
        ) {
          setApplyError(response.error.data.message[0].value as string);
        } else if (typeof response.error.data.message === 'string') {
          setApplyError(response.error.data.message);
        } else {
          setApplyError('Internal Server Error. Try Again');
        }
      }
    }
  };

  const applyApplicationOngoingWarningAction = async ({
    id,
    warningId,
    action,
    comment,
    matchStatus,
  }: {
    id: string;
    warningId: string;
    action: string;
    comment: string;
    matchStatus?: string;
  }) => {
    setApplyError('');

    const response = (await applyApplicationOngoingWarning({
      appId: applicationId as string,
      id,
      warningId,
      body: {
        action,
        comment,
        employeeId: currentEmployee?.id as string,
        matchStatus,
      },
    })) as { error: ResponseError };

    if (response.error) {
      if (response.error.data.message) {
        if (
          Array.isArray(response.error.data.message) &&
          response.error.data.message[0].property === 'coreError'
        ) {
          setApplyError(response.error.data.message[0].value as string);
        } else if (typeof response.error.data.message === 'string') {
          setApplyError(response.error.data.message);
        } else {
          setApplyError('Internal Server Error. Try Again');
        }
      }
    }
  };

  const addCommentAction = useCallback(
    (text: string) => {
      addComment({
        id: applicationId as string,
        body: {
          text,
        },
      });
    },
    [applicationId],
  );

  const onRejectAplication = async () => {
    setStatusChangeError('');

    const response = (await rejectApplication({
      id: applicationId as string,
      body: { status: NewApplicationStatus.DECLINED },
    })) as { error?: { data?: { message?: string } } };

    if (
      response.error?.data?.message &&
      !Array.isArray(response.error?.data?.message)
    ) {
      setStatusChangeError(response.error?.data?.message);
    }
  };

  const onBackToIncomplete = async () => {
    setStatusChangeError('');

    const response = (await backToIncomplete({
      id: applicationId as string,
      body: { status: NewApplicationStatus.INCOMPLETE },
    })) as { error?: { data?: { message?: string } } };

    if (
      response.error?.data?.message &&
      !Array.isArray(response.error?.data?.message)
    ) {
      setStatusChangeError(response.error?.data?.message);
    }
  };

  const onAddResponsible = async (data: {
    status: NewApplicationStatus;
    id?: string | undefined;
  }) => {
    setStatusChangeError('');

    const response = addResponsibleToApplication({
      id: applicationId as string,
      body: {
        status: data.status,
        reviewEmployeeId: data.id,
      },
    }) as { error?: { data?: { message?: string } } };

    if (
      response.error?.data?.message &&
      !Array.isArray(response.error?.data?.message)
    ) {
      setStatusChangeError(response.error?.data?.message);
    }
  };

  const getInfoAction = useCallback(async () => {
    if (applicationId) {
      const response = (await getInfo(applicationId)) as {
        error: ResponseError;
        data: any;
      };

      getApplicationChat(applicationId);

      if (activePermissions?.includes(PermissionEnum.USERS_LIST)) {
        getCustomersEmployeesList();
      }

      if (response) {
        if (response?.error?.data?.message) {
          if (
            Array.isArray(response.error.data.message) &&
            response.error.data.message[0].property === 'coreError'
          ) {
            setApplicationLoadErrorVisible(true);
          }
        }
      }
    }
  }, [applicationId, activePermissions]);

  const onCreateCustomer = async () => {
    setStatusChangeError('');

    const response = (await createNewIndividualCustomer({
      id: applicationId as string,
      body: { status: NewApplicationStatus.ACCEPTED },
    })) as { error?: { data?: { message?: string } } };

    if (
      response.error?.data?.message &&
      !Array.isArray(response.error?.data?.message)
    ) {
      setStatusChangeError(response.error?.data?.message);
    } else {
      setUserCreated(true);

      setTimeout(() => {
        getApplicationOrganization(applicationId as string);
      }, 7000);
    }
  };

  const getSystemLogsAction = useCallback(() => {
    if (applicationId) {
      getSystemLogs(applicationId);
    }
  }, [applicationId]);

  const onSubmit = handleSubmit(async (values) => {
    setApplyError('');

    const body = getIndividualApplicationBody(
      values,
      newApplicationInfo?.basePersonalInfoEditable as boolean,
      newApplicationInfo?.applicationScheme,
    );

    const response = (await updateInfo({
      body: {
        ...body,
        participatesInPolitics: Boolean(
          values.participatesInPolitics === 'yes',
        ),
        isUSResident: Boolean(values.isUSResident === 'yes'),
      },
      id: applicationId as string,
    })) as { error?: ResponseError };

    if (response?.error) {
      responseErrorsResolver({
        errors: response?.error,
        setError,
        setGlobalError: setApplyError,
      });
    }

    if (
      (newApplicationInfo?.applicationScheme &&
        (newApplicationInfo?.applicationScheme?.civilActive ||
          newApplicationInfo?.applicationScheme?.criminalOfAnyActive ||
          newApplicationInfo?.applicationScheme
            ?.lastThreeYearsAddressHistoryActive ||
          newApplicationInfo?.applicationScheme
            ?.nationalInsuranceNumberOfDirectorsActive ||
          newApplicationInfo?.applicationScheme?.regulatoryActive ||
          newApplicationInfo?.applicationScheme
            ?.businessAndEmploymentActive)) ||
      newApplicationInfo?.applicationScheme?.id ===
        'ed560559-8354-4634-b6dd-eb341dfb551a'
    ) {
      const questionnarieValues = questionnarieGetValues();

      const questionnarieBody = getCofandIndividualQuestionnarieBody({
        questionnarieValues,
        scheme: newApplicationInfo?.applicationScheme,
      });

      const questionnarieResponse = (await updateIndividualQuestionnarie({
        id: newApplicationInfo?.id,
        body: questionnarieBody,
      })) as { error?: ResponseError };

      if (questionnarieResponse?.error) {
        responseErrorsResolver({
          errors: questionnarieResponse?.error,
          setError: questionnarieSetError,
          setGlobalError: setApplyError,
        });
      }

      if (
        newApplicationInfo?.applicationScheme?.id ===
        'ed560559-8354-4634-b6dd-eb341dfb551a'
      ) {
        onSaveAccountsCurrencies();
        onSaveAccountsFlows();
        onSaveExpectedFundsOrigins();
        onSaveCRSAndFatca();
      }
    }
  });

  const retryOngoinStatusAction = async () => {
    clearTimeout(retryTimeout);

    const response = (await getOngoingMonitoringStatus(
      newApplicationInfo?.id as string,
    )) as { data?: { data?: { edges: { node: OngoingCheckingStatus }[] } } };

    if (response?.data?.data) {
      if (
        response?.data?.data?.edges?.find(
          (item) => item.node.status === OngoingStatus.SUCCESS,
        ) ||
        response?.data?.data?.edges?.find(
          (item) => item.node.status === OngoingStatus.FAIL,
        )
      ) {
        clearTimeout(retryTimeout);
      } else {
        retryTimeout = setTimeout(() => {
          retryOngoinStatusAction();
        }, 60000);
      }
    }
  };

  const restartOngoingCheckingAction = async () => {
    const response = (await restartOngoingChecking({
      applicationId: newApplicationInfo?.id as string,
    })) as { data?: { data?: OngoingCheckingStatus } };

    if (response?.data) {
      retryOngoinStatusAction();
    }
  };

  useEffect(() => {
    if ((!newApplicationInfo && applicationId) || !prevApplicationId) {
      getInfoAction();
      getSystemLogsAction();
    }
  }, [applicationId, getInfoAction, newApplicationInfo, prevApplicationId]);

  useEffect(() => {
    if (
      applicationId &&
      activePermissions?.includes(
        PermissionEnum.APPLICATIONS_CUSTOMERS_DOCUMENTS_LIST,
      )
    ) {
      getIndividualApplicationDocuments(applicationId);
    }
  }, [applicationId]);

  useEffect(() => {
    if (
      newApplicationInfo &&
      activePermissions?.includes(
        PermissionEnum.APPLICATIONS_CUSTOMERS_SCORE_LIST,
      )
    ) {
      getApplicationRiskScoreAction();
    }
  }, [newApplicationInfo, activePermissions]);

  useEffect(() => {
    if (
      amlUpdateStatus &&
      amlUpdateStatus === CustomerScoreUpdateStatus.SUCCESS &&
      activePermissions?.includes(
        PermissionEnum.APPLICATIONS_CUSTOMERS_SCORE_LIST,
      )
    ) {
      getApplicationLastCalculaingScore(applicationId as string);
    }
  }, [amlUpdateStatus, activePermissions]);

  useEffect(() => {
    if (
      amlUpdateStatus &&
      activePermissions?.includes(
        PermissionEnum.APPLICATIONS_CUSTOMERS_SCORE_LIST,
      )
    ) {
      const amlUpdateId = localStorage.getItem('applicationAmlUpdateId');

      if (amlUpdateId) {
        timeInterval = setInterval(() => {
          getApplicationLasCalculaingScoreAction(amlUpdateId);
        }, 15000);
      }
    }
  }, [amlUpdateStatus, activePermissions]);

  useEffect(() => {
    if (!validators) {
      getValidators();
    }
  }, [getValidators, validators]);

  useEffect(() => {
    if (accountsCurrencies) {
      setAccountsCurrenciesValues(
        accountsCurrencies?.map((item) => ({
          id: item.id,
          currencyId: item.currencyId,
          accountOpeningPurpose: item.accountOpeningPurpose,
          plannedTransactionNature: item.plannedTransactionNature,
        })),
      );
    }
  }, [accountsCurrencies]);

  useEffect(() => {
    if (crsAndFatca) {
      setCrsAndFatcaValues(
        crsAndFatca?.map((item) => ({
          id: item.id,
          residenceCountryId: item?.residenceCountryId,
          taxIdentificationNumber: item?.taxIdentificationNumber || '',
          noTinReason: item?.noTinReason || '',
        })),
      );
    }
  }, [crsAndFatca]);

  useEffect(() => {
    if (accountsFlows && accountsCurrencies) {
      const nextAccountsFlows = accountsFlows?.map((item) => ({
        id: item.id,
        transactionType: item.transactionType,
        currencyId: item.currencyId,
        expectedAnnualCreditTurnover:
          item.expectedAnnualCreditTurnover.toString(),
        maxTransferAmountPerTransaction:
          item.maxTransferAmountPerTransaction.toString(),
        transactionFrequency: item.transactionFrequency.toString(),
        transactionFrequencyPeriodMonths:
          item.transactionFrequencyPeriodMonths.toString(),
      }));

      const notCreatedCurrencies: AccountsCurrencies[] = [];

      accountsCurrencies?.forEach((item) => {
        if (
          !nextAccountsFlows?.find(
            (flow) => flow.currencyId === item.currencyId,
          )
        ) {
          notCreatedCurrencies.push(item);
        }
      });

      if (notCreatedCurrencies?.length) {
        notCreatedCurrencies?.forEach((item) => {
          nextAccountsFlows.push(
            {
              id: `accounts-flow-${uuid()}`,
              transactionType: 'INCOMING',
              currencyId: item.currencyId,
              expectedAnnualCreditTurnover: '',
              maxTransferAmountPerTransaction: '',
              transactionFrequency: '',
              transactionFrequencyPeriodMonths: '',
            },
            {
              id: `accounts-flow-${uuid()}`,
              transactionType: 'OUTGOING',
              currencyId: item.currencyId,
              expectedAnnualCreditTurnover: '',
              maxTransferAmountPerTransaction: '',
              transactionFrequency: '',
              transactionFrequencyPeriodMonths: '',
            },
          );
        });
      }

      setAccountsFlowsValues(nextAccountsFlows);
    }
  }, [accountsFlows, accountsCurrencies]);

  useEffect(() => {
    if (expectedFundsOrigins) {
      setExpectedFundsOriginsValues(
        expectedFundsOrigins?.map((item) => ({
          id: item.id,
          currencyId: item.currencyId,
          mainCounterpartiesAndWebsites: item.mainCounterpartiesAndWebsites,
          countryOfIncorporationId: item.countryOfIncorporationId,
          counterpartyBankCountryIds: item.counterpartyBankCountryIds,
          activityCountryIds: item.activityCountryIds,
          mainActivities: item.mainActivities,
          transactionReason: item.transactionReason,
          transactionType: item.transactionType,
        })),
      );
    }
  }, [expectedFundsOrigins]);

  useEffect(() => {
    if (deleteAccountsCurrenciesIsSuccess) {
      setSelectedAccountsCurrencyId('');
    }
  }, [deleteAccountsCurrenciesIsSuccess]);

  useEffect(() => {
    if (deleteExpectedFundsOriginsIsSuccess) {
      setSelectedFundsOriginsId('');
    }
  }, [deleteExpectedFundsOriginsIsSuccess]);

  useEffect(() => {
    if (deleteCrsAndFatcaIsSuccess) {
      setSelectedCrsAndFatcaId('');
    }
  }, [deleteCrsAndFatcaIsSuccess]);

  useEffect(() => {
    if (
      newApplicationInfo &&
      questionnarieId &&
      Boolean(
        newApplicationInfo?.applicationScheme?.id ===
        'ed560559-8354-4634-b6dd-eb341dfb551a',
      )
    ) {
      const nextAllowedCategories =
        newApplicationInfo?.applicationScheme?.documentsCategories?.edges?.map(
          (item) => item.node,
        ) as AllowedCategory[];

      nextAllowedCategories?.unshift({
        id: 'identification-category',
        name: 'Identification documents',
        required: false,
      });

      dispatch(setAllowedDocumentsCategories(nextAllowedCategories));

      if (!accountsCurrencies) {
        getAccountsCurrencies(questionnarieId);
      }

      if (!accountsFlows) {
        getAccountsFlows(questionnarieId);
      }

      if (!expectedFundsOrigins) {
        getExpectedFundsOrigins(questionnarieId);
      }

      if (!crsAndFatca) {
        getCrsAndFatca(questionnarieId);
      }
    }
  }, [
    questionnarieId,
    newApplicationInfo,
    accountsCurrencies,
    accountsFlows,
    crsAndFatca,
  ]);

  useEffect(() => {
    if (
      newApplicationInfo &&
      newApplicationInfo.status !== NewApplicationStatus.INCOMPLETE &&
      activePermissions?.includes(
        PermissionEnum.APPLICATIONS_CUSTOMERS_AML_LIST,
      )
    ) {
      retryOngoinStatusAction();
    }
  }, [newApplicationInfo]);

  useEffect(() => {
    if (
      ongoingCheckingStatus &&
      ongoingCheckingStatus?.status === OngoingStatus.SUCCESS &&
      newApplicationInfo &&
      newApplicationInfo.status !== NewApplicationStatus.INCOMPLETE &&
      activePermissions?.includes(
        PermissionEnum.APPLICATIONS_CUSTOMERS_AML_LIST,
      )
    ) {
      getApplicationOngoingMonitoring(newApplicationInfo.id);
    }
  }, [ongoingCheckingStatus, newApplicationInfo]);

  useEffect(() => {
    if (isSuccess && riskScore) {
      setSuccessMessageVisible(true);

      setTimeout(() => {
        setSuccessMessageVisible(false);
      }, 6000);
    }
  }, [isSuccess]);

  useEffect(() => {
    if (rejectIsSuccess || rejectIsError) {
      setPopupVisible(false);
    }
  }, [rejectIsSuccess, rejectIsError]);

  useEffect(() => {
    if (
      prevClientActivitiy &&
      prevClientActivitiy === employmentActivity?.value &&
      fields.clientActivityId !== employmentActivity?.value
    ) {
      setValue('nameOfEmployer', '');
      setValue('countryOfEmployerId', '');
      setValue('employeePosition', '');
      questionnarieSetValue('natureOfBusinessActivity', '');
    }

    if (
      prevClientActivitiy &&
      prevClientActivitiy === businessOwnerActivity?.value &&
      fields.clientActivityId !== businessOwnerActivity?.value
    ) {
      setValue('countryOfEmployerId', '');
      questionnarieSetValue('nameOfCompany', '');
      questionnarieSetValue('ownership', '');
      questionnarieSetValue('natureOfBusinessActivity', '');
    }

    if (
      prevClientActivitiy &&
      prevClientActivitiy ===
        clientsActivities?.find((item) => item.name === 'Self-employed')
          ?.value &&
      fields.clientActivityId !==
        clientsActivities?.find((item) => item.name === 'Self-employed')?.value
    ) {
      questionnarieSetValue('natureOfBusinessActivity', '');
    }

    if (
      prevClientActivitiy &&
      prevClientActivitiy === otherActivity?.value &&
      fields.clientActivityId !== otherActivity?.value
    ) {
      setValue('typeOfActivityDescription', '');
    }
  }, [fields.clientActivityId, prevClientActivitiy]);

  useEffect(() => {
    if (
      prevSourceOfFunds &&
      prevSourceOfFunds.includes(otherSourceOfFunds?.value) &&
      !fields.sourceOfFundsIds?.includes(otherSourceOfFunds?.value as string)
    ) {
      setValue('sourceOfFundsDescription', '');
    }
  }, [fields.sourceOfFundsIds, prevSourceOfFunds]);

  useEffect(() => {
    if (paginationIsSuccess) {
      setSelectedWarningGroup('');
    }
  }, [paginationIsSuccess]);

  useEffect(() => {
    if (
      activePermissions?.includes(PermissionEnum.DICTIONARIES_ITEMS_LIST) &&
      newApplicationInfo &&
      Boolean(
        newApplicationInfo?.applicationScheme?.id ===
        'ed560559-8354-4634-b6dd-eb341dfb551a',
      )
    ) {
      if (!sourceOfWealthForIndividual) {
        getSourceOfWealthForIndividualDictionary();
      }

      if (!sourceOfIncome) {
        getSourceOfIncomeDictionary();
      }

      if (!sourceOfWealth) {
        getCustomerSourceOfWealth();
      }

      if (!customerSourceOfFunds) {
        getCustomerSourceOfFunds();
      }
    }
  }, [
    activePermissions,
    sourceOfWealthForIndividual,
    sourceOfIncome,
    newApplicationInfo,
    sourceOfWealth,
    customerSourceOfFunds,
  ]);

  useEffect(() => {
    if (newApplicationInfo) {
      const formValues = {
        firstName: newApplicationInfo.firstName || '',
        lastName: newApplicationInfo.lastName || '',
        phone: newApplicationInfo.phone || '',
        email: newApplicationInfo.email || '',
        monthlyTurnoverRangeId: newApplicationInfo.monthlyTurnoverRangeId || '',
        monthlyNumberIncomingTransactionsRangeId:
          newApplicationInfo.monthlyNumberIncomingTransactionsRangeId || '',
        monthlyNumberOutgoingTransactionsRangeId:
          newApplicationInfo.monthlyNumberOutgoingTransactionsRangeId || '',
        monthlyValueIncomingTransactionsRangeId:
          newApplicationInfo.monthlyValueIncomingTransactionsRangeId || '',
        monthlyValueOutgoingTransactionsRangeId:
          newApplicationInfo.monthlyValueOutgoingTransactionsRangeId || '',
        monthlyMaxValueIncomingPaymentRangeId:
          newApplicationInfo.monthlyMaxValueIncomingPaymentRangeId || '',
        monthlyMaxValueOutgoingPaymentRangeId:
          newApplicationInfo.monthlyMaxValueOutgoingPaymentRangeId || '',
        dob: newApplicationInfo.dob || '',
        countryId: newApplicationInfo?.country?.id || '',
        city: newApplicationInfo.city || '',
        postalCode: newApplicationInfo.postalCode || '',
        address1: newApplicationInfo.address1 || '',
        address2: newApplicationInfo.address2 || '',
        passportNumber: newApplicationInfo.passportNumber || '',
        passportExpiredDate: newApplicationInfo.passportExpiredDate || '',
        participatesInPolitics: !newApplicationInfo.participatesInPolitics
          ? 'no'
          : 'yes',
        isUSResident: !newApplicationInfo.isUSResident ? 'no' : 'yes',
        userId: newApplicationInfo.userId,
        sourceOfFundsIds:
          newApplicationInfo?.sourcesOfFunds?.edges.map(
            (item) => item.node.id,
          ) || [],
        sourceOfFundsDescription:
          newApplicationInfo.sourceOfFundsDescription || '',
        clientActivityId: newApplicationInfo?.clientActivityId || '',
        deliveryChannelId: newApplicationInfo?.deliveryChannelId || '',
        productIds:
          newApplicationInfo?.products?.edges.map((item) => item.node.id) || [],
        nationalityId: newApplicationInfo?.nationalityId || '',
        sumSubStatus: newApplicationInfo?.sumSubApplication?.reviewAnswer || '',
        typeOfActivityDescription:
          newApplicationInfo?.typeOfActivityDescription || '',
        nameOfEmployer: newApplicationInfo?.nameOfEmployer || '',
        employeePosition: newApplicationInfo?.employeePosition || '',
        countryOfEmployerId: newApplicationInfo?.countryOfEmployerId || '',
        taxNumber: newApplicationInfo?.taxNumber || '',
        birthCountryId: newApplicationInfo?.birthCountryId || '',
        placeOfBirth: newApplicationInfo?.placeOfBirth || '',
        correspondenceAddressSameAsRegisteredAddress:
          newApplicationInfo?.correspondenceAddressSameAsRegisteredAddress,
        correspondenceCountryId:
          newApplicationInfo?.correspondenceCountryId || '',
        correspondenceCity: newApplicationInfo?.correspondenceCity || '',
        correspondencePostalCode:
          newApplicationInfo?.correspondencePostalCode || '',
        correspondenceAddress1:
          newApplicationInfo?.correspondenceAddress1 || '',
        correspondenceAddress2:
          newApplicationInfo?.correspondenceAddress2 || '',
      };

      setDefaultFormValues(formValues);
      reset(formValues);

      getApplicationAgreements(newApplicationInfo.id);
    }
  }, [reset, dispatch, newApplicationInfo]);

  useEffect(() => {
    if (newApplicationInfo) {
      if (newApplicationInfo?.applicationScheme) {
        if (
          newApplicationInfo?.applicationScheme?.civilActive ||
          newApplicationInfo?.applicationScheme?.criminalOfAnyActive ||
          newApplicationInfo?.applicationScheme
            ?.lastThreeYearsAddressHistoryActive ||
          newApplicationInfo?.applicationScheme
            ?.nationalInsuranceNumberOfDirectorsActive ||
          newApplicationInfo?.applicationScheme?.regulatoryActive ||
          newApplicationInfo?.applicationScheme?.businessAndEmploymentActive ||
          newApplicationInfo?.hasQuestionnaire
        ) {
          if (!questionnarie) {
            getApplicationQuestionnarie(newApplicationInfo?.id);
          }
        }
      }
    }
  }, [newApplicationInfo]);

  useEffect(() => {
    if (questionnarie) {
      questionnarieReset(questionnarie);
      setDefaultQuestionnarieFormValues(questionnarie);
    }
  }, [questionnarie]);

  useEffect(() => {
    setTimeout(() => {
      dispatch(checkChatMessages());
    }, 60000);
  }, [dispatch]);

  useEffect(() => {
    if (prevStepsStatus && !isEqual(stepsStatus, prevStepsStatus)) {
      dispatch(updateStepsStatus(stepsStatus));
    }
  }, [stepsStatus, prevStepsStatus, dispatch]);

  useEffect(() => {
    if ((addResponsibleIsSuccess || addResponsibleIsError) && applicationId) {
      getIndividualApplicationDocuments(applicationId);
    }
  }, [addResponsibleIsSuccess, applicationId, addResponsibleIsError]);

  useEffect(() => {
    const handleBeforeUnload = (event: {
      preventDefault: () => void;
      returnValue: string;
    }) => {
      event.preventDefault();

      // eslint-disable-next-line no-param-reassign
      event.returnValue = '';
    };

    if (
      !isEqual(fields, defaultFormValues) ||
      !isEqual(questionnarieFields, defaultQuestionnarieFormValues)
    ) {
      window.addEventListener('beforeunload', handleBeforeUnload);
    } else {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    }

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [
    fields,
    defaultFormValues,
    questionnarieFields,
    defaultQuestionnarieFormValues,
  ]);

  useEffect(() => {
    let title = 'New Application';

    const companyName = localStorage.getItem('companyName');

    if (companyName) {
      title = `${title} | ${companyName}`;
    }

    document.title = title;
  }, []);

  let content;

  if (
    !userCreated &&
    (isLoading ||
      isFetching ||
      infoDictionariesIsLoading ||
      questionnarieIsFetching ||
      questionnarieIsLoading)
  ) {
    content = (
      <div className={cn.newApplicationInfoContentLoader}>
        <IconLoader />
      </div>
    );
  } else if (
    (!isLoading &&
      !isFetching &&
      !infoDictionariesIsLoading &&
      !questionnarieIsFetching &&
      !questionnarieIsLoading &&
      activePermissions?.includes(
        PermissionEnum.APPLICATIONS_CUSTOMERS_LIST,
      )) ||
    userCreated
  ) {
    if (applicationLoadErrorVisible) {
      content = (
        <div className={cn.applicationLoadError}>
          <div className={cn.applicationLoadErrorTitle}>
            Something went wrong. Reload page
          </div>
          <ButtonPrimary
            text="Reload"
            className={cn.reloadButton}
            onClick={() => window.location.reload()}
            bold
          />
        </div>
      );
    } else {
      content = (
        <div className={cn.newApplicationInfoContent}>
          <NewApplicationInfoTop
            status={newApplicationInfo?.status}
            reviewStatus={newApplicationInfo?.reviewStatus}
            setPopupVisible={(data) => setPopupVisible(data)}
            stepsStatus={applicationStepsStatus}
            setMatchSelected={(data) => dispatch(setMatchSelected(data))}
            onSendComment={(text) => addCommentAction(text)}
            matches={newApplicationInfo?.similarApplications?.edges}
            reviewedDuplicates={newApplicationInfo?.reviewedDuplicates}
            applicationId={applicationId}
            comments={newApplicationInfo?.comments?.edges?.map(
              (item) => item?.node,
            )}
            onSubmit={onSubmit as OnSubmitType}
            requestInProgress={
              requestInProgress ||
              questionnarieUpdateInProgress ||
              createAccountsCurrenciesIsProgress ||
              createAccountsFlowsIsProgress ||
              createExpectedFundsOriginsInProgress ||
              updateAccountsCurrenciesInProgress ||
              updateAccountsFlowsInProgress ||
              updateExpectedFundsOriginsInProgress ||
              createCrsAndFatcaIsProgress ||
              updateCrsAndFatcaInProgress
            }
            sendCommentInProgress={sendCommentInProgress}
            sendCommentIsSuccess={sendCommentIsSuccess}
            onCreate={() => onCreateCustomer()}
            onBackToIncomplete={() => onBackToIncomplete()}
            backToIncompleteInProgress={backToIncompleteInProgress}
            createInProgress={createInProgress}
            questionnarieFields={questionnarieFields}
            fields={fields}
            createdDate={newApplicationInfo?.createdAt as string}
            defaultFormValues={defaultFormValues}
            defaultQuestionnarieFormValues={defaultQuestionnarieFormValues}
            reviewDuplicatesComment={
              newApplicationInfo?.reviewDuplicatesComment
            }
            systemLogs={systemLogs}
            applicationType={ApplicationType.INDIVIDUAL}
            isEditable={activePermissions?.includes(
              PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
            )}
            createAvailable={activePermissions?.includes(
              PermissionEnum.APPLICATIONS_CUSTOMERS_CREATE_ACTION,
            )}
            statusChangeError={statusChangeError}
            organizationId={newApplicationInfo?.organizationId}
            // phone={newApplicationInfo?.externalUserId}
            // email={newApplicationInfo?.email}
            reviewEmployee={newApplicationInfo?.reviewEmployee}
            onAddResponsible={(data) => onAddResponsible(data)}
            canSelectResponsible={activePermissions?.includes(
              PermissionEnum.USERS_LIST,
            )}
            addResponsibleInProgress={addResponsibleInProgress}
            addResponsibleIsSuccess={addResponsibleIsSuccess}
            employeesList={employeesList}
            title={
              newApplicationInfo
                ? `${newApplicationInfo.firstName} ${newApplicationInfo.lastName}`
                : 'New Application'
            }
            applicationChat={applicationChat ? applicationChat[0] : undefined}
            riskScore={riskScore}
            riskScoreIsLoading={riskScoreIsLoading}
            applicationScheme={newApplicationInfo?.applicationScheme}
            setError={setError}
            basePersonalInfoEditable={
              newApplicationInfo?.basePersonalInfoEditable
            }
            clearErrors={clearErrors}
            questionnarieSetError={questionnarieSetError}
            questionnarieClearErrors={questionnarieClearErrors}
            changeLogs={changeLogs}
            changeLogsPagination={changeLogsPagination}
            client={newApplicationInfo?.user}
            riskScoreUpdateIsSuccess={isSuccess}
            riskScoreRequestInProgress={riskScoreRequestInProgress}
            amlUpdateStatus={amlUpdateStatus}
            onUpdate={onUpdate}
            getApplicationRiskScore={getApplicationRiskScoreAction}
            stepOneBlockLoadingInProgress={
              createAccountsCurrenciesIsProgress ||
              createAccountsFlowsIsProgress ||
              createExpectedFundsOriginsInProgress ||
              updateAccountsCurrenciesInProgress ||
              updateAccountsFlowsInProgress ||
              updateExpectedFundsOriginsInProgress ||
              createCrsAndFatcaIsProgress ||
              updateCrsAndFatcaInProgress
            }
            onSaveAccountsCurrencies={onSaveAccountsCurrencies}
            accountsCurrenciesValues={accountsCurrenciesValues
              ?.filter((item) => item.currencyId)
              ?.map((item) => ({
                currencyId: item.currencyId,
                accountOpeningPurpose: item.accountOpeningPurpose,
                plannedTransactionNature: item.plannedTransactionNature,
              }))}
            accountsCurrencies={accountsCurrencies?.map((item) => ({
              currencyId: item.currencyId,
              accountOpeningPurpose: item.accountOpeningPurpose,
              plannedTransactionNature: item.plannedTransactionNature,
            }))}
            setHaveSecondPassport={setHaveSecondPassport}
            haveSecondPassport={haveSecondPassport}
            onSaveAccountsFlows={onSaveAccountsFlows}
            accountsFlowsValues={accountsFlowsValues
              ?.filter((item) => item.currencyId)
              ?.map((item) => ({
                transactionType: item.transactionType,
                currencyId: item.currencyId,
                expectedAnnualCreditTurnover:
                  item.expectedAnnualCreditTurnover?.toString() || '',
                maxTransferAmountPerTransaction:
                  item.maxTransferAmountPerTransaction?.toString() || '',
                transactionFrequency:
                  item.transactionFrequency?.toString() || '',
                transactionFrequencyPeriodMonths:
                  item.transactionFrequencyPeriodMonths?.toString() || '',
              }))}
            accountsFlows={accountsFlows?.map((item) => ({
              transactionType: item.transactionType,
              currencyId: item.currencyId,
              expectedAnnualCreditTurnover:
                item.expectedAnnualCreditTurnover?.toString() || '',
              maxTransferAmountPerTransaction:
                item.maxTransferAmountPerTransaction?.toString() || '',
              transactionFrequency: item.transactionFrequency?.toString() || '',
              transactionFrequencyPeriodMonths:
                item.transactionFrequencyPeriodMonths?.toString() || '',
            }))}
            onSaveExpectedFundsOrigins={onSaveExpectedFundsOrigins}
            expectedFundsOriginsValues={expectedFundsOriginsValues
              ?.filter((item) => item.currencyId)
              ?.map((item) => ({
                currencyId: item.currencyId,
                mainCounterpartiesAndWebsites:
                  item?.mainCounterpartiesAndWebsites || '',
                countryOfIncorporationId: item?.countryOfIncorporationId || '',
                counterpartyBankCountryIds:
                  item?.counterpartyBankCountryIds || [],
                activityCountryIds: item?.activityCountryIds || [],
                mainActivities: item?.mainActivities || '',
                transactionReason: item?.transactionReason || '',
                transactionType: item?.transactionType || '',
              }))}
            expectedFundsOrigins={expectedFundsOrigins?.map((item) => ({
              currencyId: item.currencyId,
              mainCounterpartiesAndWebsites:
                item?.mainCounterpartiesAndWebsites || '',
              countryOfIncorporationId: item?.countryOfIncorporationId || '',
              counterpartyBankCountryIds:
                item?.counterpartyBankCountryIds || [],
              activityCountryIds: item?.activityCountryIds || [],
              mainActivities: item?.mainActivities || '',
              transactionReason: item?.transactionReason || '',
              transactionType: item?.transactionType || '',
            }))}
            onSaveCrsAndFatca={onSaveCRSAndFatca}
            crsAndFatcaValues={crsAndFatcaValues
              ?.filter((item) => item.residenceCountryId)
              ?.map((item) => ({
                residenceCountryId: item?.residenceCountryId || '',
                taxIdentificationNumber: item?.taxIdentificationNumber || '',
                noTinReason: item?.noTinReason,
              }))}
            crsAndFatca={crsAndFatca?.map((item) => ({
              residenceCountryId: item?.residenceCountryId,
              taxIdentificationNumber: item?.taxIdentificationNumber || '',
              noTinReason: item?.noTinReason || '',
            }))}
          />
          <StepItem
            title="Step 1"
            status={applicationStepsStatus?.stepOne as NewApplicationStepStatus}
            id="step-1"
          >
            <StepOne
              control={control}
              questionnarieControl={questionnarieControl}
              errors={errors}
              questionnarieErrors={questionnarieErrors}
              status={newApplicationInfo?.status}
              clientActivity={fields.clientActivityId}
              sourceOfFundsIds={fields.sourceOfFundsIds}
              isEditable={
                activePermissions?.includes(
                  PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                ) && newApplicationInfo?.basePersonalInfoEditable
              }
              hasSumSubId={Boolean(
                newApplicationInfo?.sumSubApplication?.applicantId,
              )}
              activePermissions={activePermissions}
              applicationScheme={newApplicationInfo?.applicationScheme}
              clearErrors={clearErrors}
              questionnarieClearErrors={questionnarieClearErrors}
              regulatedCompanyScheme={
                newApplicationInfo?.applicationScheme?.id ===
                'ed560559-8354-4634-b6dd-eb341dfb551a'
              }
              questionnarieFields={questionnarieFields}
              sourceOfWealth={sourceOfWealth}
              baseCurrency={baseCurrency}
              questionnarieSetValue={questionnarieSetValue}
              sourceOfIncome={sourceOfIncome}
              sourceOfWealthForIndividual={sourceOfWealthForIndividual}
              fields={fields}
              groupStructurChartFile={documents?.find(
                (item) =>
                  item.documentCategoryId ===
                  '72b810fc-d635-4937-8a14-f9b77eaa699f',
              )}
            />
          </StepItem>
          <StepItem
            title="Step 2"
            status={applicationStepsStatus?.stepTwo as NewApplicationStepStatus}
            id="step-2"
          >
            <StepTwo
              control={control}
              status={newApplicationInfo?.status}
              isEditable={activePermissions?.includes(
                PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
              )}
              errors={errors}
              clearErrors={clearErrors}
              questionnarieClearErrors={questionnarieClearErrors}
              questionnarieControl={questionnarieControl}
              questionnarieErrors={questionnarieErrors}
              questionnarieSetValue={questionnarieSetValue}
              questionnarieFields={questionnarieFields}
              accountsCurrenciesIsLoading={accountsCurrenciesIsLoading}
              accountsCurrenciesIsFetching={accountsCurrenciesIsFetching}
              currencies={currenciesList}
              accountsCurrenciesValues={accountsCurrenciesValues}
              setAccountsCurrenciesValues={setAccountsCurrenciesValues}
              onDeleteAccountCurrencies={onDeleteAccountCurrencies}
              selectedAccountsCurrencyId={selectedAccountsCurrencyId}
              deleteAccountsCurrenciesInProgress={
                deleteAccountsCurrenciesInProgress
              }
              setAccountsFlowsValues={setAccountsFlowsValues}
              accountsFlowsValues={accountsFlowsValues}
              customerSourceOfFunds={customerSourceOfFunds}
              regulatedCompanyScheme={
                newApplicationInfo?.applicationScheme?.id ===
                'ed560559-8354-4634-b6dd-eb341dfb551a'
              }
              accountsFlowsIsLoading={accountsFlowsIsLoading}
              accountsFlowsIsFetching={accountsFlowsIsFetching}
              currenciesList={currencies}
              expectedFundsOriginsIsLoading={expectedFundsOriginsIsLoading}
              expectedFundsOriginsIsFetching={expectedFundsOriginsIsFetching}
              deleteExpectedFundsOriginsInProgress={
                deleteExpectedFundsOriginsInProgress
              }
              onDeleteExpectedFundsOrigins={onDeleteExpectedFundsOrigins}
              selectedExpectedFundsOriginsId={selectedExpectedFundsOriginsId}
              expectedFundsOriginsValues={expectedFundsOriginsValues}
              setExpectedFundsOriginsValues={setExpectedFundsOriginsValues}
              countries={countries}
            />
          </StepItem>
          {(!newApplicationInfo?.applicationScheme ||
            (newApplicationInfo.applicationScheme &&
              newApplicationInfo.applicationScheme?.kycProvider !==
                'NONE')) && (
            <StepItem
              title="Step 3"
              status={
                applicationStepsStatus?.stepThree as NewApplicationStepStatus
              }
              id="step-3"
            >
              <StepThree
                sumSubStatus={
                  newApplicationInfo?.sumSubApplication?.reviewAnswer
                }
                sumSubReviewStatus={
                  newApplicationInfo?.sumSubApplication?.reviewStatus
                }
                // phone={newApplicationInfo?.externalUserId}
                id={
                  newApplicationInfo?.sumSubApplication?.applicantId as string
                }
                changeLogsAvailable={activePermissions?.includes(
                  PermissionEnum.AUDIT_LOG_LIST,
                )}
                sumSubId={newApplicationInfo?.sumSubApplication?.id}
                search={search}
                onGroupSelect={(data) => dispatch(onGroupSelect(data))}
                countries={countries}
                control={control}
                documents={documents}
                selectedDocumentsCounts={selectedDocumentsCounts}
                status={newApplicationInfo?.status}
                applicationId={applicationId}
                documentsVisible={activePermissions?.includes(
                  PermissionEnum.APPLICATIONS_CUSTOMERS_DOCUMENTS_LIST,
                )}
                // email={newApplicationInfo?.email}
                isEditable={activePermissions?.includes(
                  PermissionEnum.APPLICATIONS_CUSTOMERS_EDIT_ACTION,
                )}
                setSelected={({ id, selected }) =>
                  dispatch(setSelected({ id, selected }))
                }
                sumSubDocuments={newApplicationInfo?.idDocs?.edges?.map(
                  (item) => item.node,
                )}
                errors={errors}
                renameAvailable={activePermissions?.includes(
                  PermissionEnum.APPLICATIONS_CUSTOMERS_DOCUMENTS_EDIT_ACTION,
                )}
                regulatedCompanyScheme={
                  newApplicationInfo?.applicationScheme?.id ===
                  'ed560559-8354-4634-b6dd-eb341dfb551a'
                }
                clearErrors={clearErrors}
                fields={fields}
                questionnarieControl={questionnarieControl}
                questionnarieClearErrors={questionnarieClearErrors}
                questionnarieErrors={questionnarieErrors}
                questionnarieFields={questionnarieFields}
                questionnarieSetValue={questionnarieSetValue}
                selectedCrsAndFatcaId={selectedCrsAndFatcaId}
                crsAndFatcaIsLoading={crsAndFatcaIsLoading}
                crsAndFatcaIsFetching={crsAndFatcaIsFetching}
                deleteCrsAndFatcaInProgress={deleteCrsAndFatcaInProgress}
                onDeleteCRSAndFatca={onDeleteCRSAndFatca}
                crsAndFatcaValues={crsAndFatcaValues}
                setCrsAndFatcaValues={setCrsAndFatcaValues}
                setHaveSecondPassport={setHaveSecondPassport}
                haveSecondPassport={haveSecondPassport}
                setValue={setValue}
                allowedDocumentsCategories={allowedDocumentsCategories}
                activePermissions={activePermissions}
              />
            </StepItem>
          )}

          <StepItem
            title={
              !newApplicationInfo?.applicationScheme ||
              (newApplicationInfo.applicationScheme &&
                newApplicationInfo.applicationScheme?.kycProvider !== 'NONE')
                ? 'Step 4'
                : 'Step 3'
            }
            status={
              applicationStepsStatus?.stepFive as NewApplicationStepStatus
            }
            id="step-4"
          >
            <StepFive
              status={newApplicationInfo?.status}
              currentEmployee={currentEmployee}
              activePermissions={activePermissions}
              isLoading={
                ongoingIsLoading || ongoingIsFetching || ongoingStatusIsLoading
              }
              ongoingMonitoring={ongoingMonitoring}
              isAllowed={activePermissions?.includes(
                PermissionEnum.APPLICATIONS_CUSTOMERS_AML_LIST,
              )}
              applyError={applyError}
              applyWarningInProgress={applyWarningInProgress}
              applyOngoingWarning={applyApplicationOngoingWarningAction}
              batchApplyWarningInProgress={batchApplyWarningInProgress}
              batchApplyOngoingWarning={
                batchApplyApplicationOngoingWarningAction
              }
              ongoingCheckingStatus={ongoingCheckingStatus}
              restartOngoingCheckingAction={restartOngoingCheckingAction}
              restartInProgress={restartInProgress}
              batchApplyWarningIsSuccess={batchApplyWarningIsSuccess}
              onResetTransactionWarningAction={onResetTransactionWarningAction}
              resetTransactionsWarningIsLoading={
                resetTransactionsWarningIsLoading
              }
              resetTransactionsWarningIsSuccess={
                resetTransactionsWarningIsSuccess
              }
              selectedWarningId={selectedWarningId}
              getApplicationWarningsAction={getApplicationWarningsAction}
              paginationInProgress={
                paginationInProgress || paginationIsFetching
              }
              selectedWarningGroup={selectedWarningGroup}
              blacklistData={blacklistData}
              setBlacklistData={setBlacklistData}
              blacklistDataVisible={blacklistDataVisible}
              setBlacklistDataVisible={setBlacklistDataVisible}
              blacklistItem={blacklistItem}
              setBlacklistDataItem={setBlacklistDataItem}
              blacklistItemVisible={blacklistItemVisible}
              setBlacklistItemVisible={setBlacklistItemVisible}
              getOngoingWarningById={getApplicationOngoingWarningByIdAction}
              isWarningByIdLoading={isWarningByIdLoading}
              isWarningByIdFetching={isWarningByIdFetching}
            />
          </StepItem>
          {(!newApplicationInfo?.applicationScheme ||
            (newApplicationInfo.applicationScheme &&
              newApplicationInfo.applicationScheme.agreementsActive) ||
            (newApplicationInfo.applicationScheme &&
              newApplicationInfo.applicationScheme.agreementsActive ===
                null)) && (
            <StepItem
              title={
                !newApplicationInfo?.applicationScheme ||
                (newApplicationInfo.applicationScheme &&
                  newApplicationInfo.applicationScheme?.kycProvider !== 'NONE')
                  ? 'Step 5'
                  : 'Step 4'
              }
              status={
                applicationStepsStatus?.stepFour as NewApplicationStepStatus
              }
              id="step-5"
            >
              <StepFour list={agreements} errors={errors} />
            </StepItem>
          )}
        </div>
      );
    }
  } else {
    // eslint-disable-next-line no-lonely-if
    if (!userCreated) {
      content = <PermissionDenied />;
    }
  }

  return (
    <div className={cn.newApplicationInfo}>
      <div className={cn.newApplicationLinkWrapper}>
        <button
          type="button"
          className={cn.newApplicationBackLink}
          onClick={() => {
            if (isDirty || questionnarieIsDirty) {
              setDirtyPopupVisible(true);
            } else {
              navigate(
                `${ROUTES.CUSTOMERS_NEW_APPLICATION_LIST}${search || ''}`,
              );
              dispatch(clearNewApplicationInfoState());
            }
          }}
        >
          <IconArrowLeft width={13} height={11} />
          Back
        </button>
      </div>
      {content}
      <Outlet />
      {hintVisible && (
        <div className={cn.customerCreatedSuccessful}>
          <div className={cn.customerCreatedSuccessfulLeft}>
            <span>
              <IconSelected />
            </span>
            <span>Customer created</span>
          </div>
          <div className={cn.customerCreatedSuccessfulRight}>
            <span>
              <NavLink
                to={generatePath(ROUTES.CUSTOMERS_INDIVIDUAL_PAGE, {
                  customerId: (newApplicationInfo?.userId as string) || '',
                })}
              >
                <IconArrowRight />
                Open application
              </NavLink>
            </span>
            <button
              type="button"
              className={cn.hintCloseButton}
              onClick={() => dispatch(setHintVisible(false))}
            >
              Close
            </button>
          </div>
        </div>
      )}
      <Popup
        title="Reject application?"
        description="Application will be closed"
        onCancelClick={() => setPopupVisible(false)}
        onOkClick={() => {
          onRejectAplication();
        }}
        cancelButtonText="Cancel"
        okButtonText={rejectInProgress ? 'Loading...' : 'Reject'}
        isVisible={popupVisible}
      />
      <Popup
        title="You have unsaved changes"
        description=""
        onCancelClick={() => setDirtyPopupVisible(false)}
        onOkClick={() =>
          navigate(`${ROUTES.CUSTOMERS_NEW_APPLICATION_LIST}${search || ''}`)
        }
        cancelButtonText="Cancel"
        okButtonText="Close"
        isVisible={dirtyPopupVisible}
      />
      {successMessageVisible && (
        <div className={cn.successAppliedMessage}>
          <IconSelected />
          <div>Risk Score successfully updated to the Customer Application</div>
        </div>
      )}
      <div id="signature-popup" />
    </div>
  );
};

export default NewApplicationInfo;
